import { useState, useRef } from 'react';
import { compressImage, isValidImageType } from '../../utils/imageUtils';
import type { RecoveryStep, AdditionalContext } from '../../types/api';

interface MismatchRecoveryProps {
  /** Current view in the recovery flow */
  recoveryStep: RecoveryStep;
  /** Original plant photo URL for context display */
  originalPhotoUrl: string;
  /** Name of the wrong identification for context */
  wrongIdentificationName: string;
  /** Loading state during re-identification */
  isProcessing: boolean;
  /** Error message to display */
  error: string | null;
  /** Callback when description context is submitted */
  onSubmitContext: (context: AdditionalContext) => void;
  /** Callback when a new photo is captured for retake */
  onRetakeCapture: (file: File, previewUrl: string) => void;
  /** Callback to navigate between recovery views */
  onSetView: (view: RecoveryStep) => void;
  /** Callback to enter name manually (preserves original photo) */
  onManualEntry: () => void;
}

const MIN_DESCRIPTION_LENGTH = 10;

const DESCRIPTION_EXAMPLES = [
  'My leaves are more pointed',
  'My plant has variegated leaves',
  'The stems are thicker',
  'It has fuzzy leaves with purple undersides',
  'Trailing vine with heart-shaped leaves',
];

const RETAKE_TIPS = [
  {
    icon: (
      <svg className="h-5 w-5 flex-shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
      </svg>
    ),
    title: 'Show the whole plant',
    description: 'Fill the frame from about 1 foot away, showing growth habit and branching.',
  },
  {
    icon: (
      <svg className="h-5 w-5 flex-shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
      </svg>
    ),
    title: 'Close-up of a single leaf',
    description: 'Show veins, edges, and surface texture. Flip it over if you can — the underside often holds more diagnostic clues.',
  },
  {
    icon: (
      <svg className="h-5 w-5 flex-shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Show where leaves meet the stem',
    description: 'Leaf arrangement (opposite, alternate, whorled) is the #1 differentiator between similar species.',
  },
  {
    icon: (
      <svg className="h-5 w-5 flex-shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
    title: 'Include flowers or fruits',
    description: 'If present, these are the single most discriminating feature for identification.',
  },
];

export function MismatchRecovery({
  recoveryStep,
  originalPhotoUrl,
  wrongIdentificationName,
  isProcessing,
  error,
  onSubmitContext,
  onRetakeCapture,
  onSetView,
  onManualEntry,
}: MismatchRecoveryProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [description, setDescription] = useState('');
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Handle photo selection for retake
  const handlePhotoSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isValidImageType(file)) {
      setPhotoError('Please select a valid image (JPEG, PNG, or WebP)');
      return;
    }

    setIsUploadingPhoto(true);
    setPhotoError(null);

    try {
      const { file: compressedFile, dataUrl } = await compressImage(file);
      onRetakeCapture(compressedFile, dataUrl);
    } catch (err) {
      console.error('Failed to process photo:', err);
      setPhotoError('Failed to process image. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Open file picker
  const openFilePicker = (capture?: 'environment') => {
    if (fileInputRef.current) {
      if (capture) {
        fileInputRef.current.setAttribute('capture', capture);
      } else {
        fileInputRef.current.removeAttribute('capture');
      }
      fileInputRef.current.click();
    }
  };

  // Submit description for re-identification
  const handleSubmitDescription = () => {
    if (description.trim().length < MIN_DESCRIPTION_LENGTH) return;
    onSubmitContext({ userDescription: description.trim() });
  };

  // Render options view — two cards side by side
  const renderOptions = () => (
    <div className="space-y-6">
      {/* Context banner */}
      {originalPhotoUrl && (
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="flex items-center gap-3">
            <img
              src={originalPhotoUrl}
              alt="Original plant photo"
              className="h-16 w-16 rounded-lg object-cover"
            />
            <div>
              <p className="text-sm font-medium text-editorial-black">
                Not {wrongIdentificationName}?
              </p>
              <p className="text-xs text-gray-500">
                Let&apos;s try to get a better match.
              </p>
            </div>
          </div>
        </div>
      )}

      <p className="text-sm text-gray-500">
        Choose how you&apos;d like to help us identify your plant:
      </p>

      {/* Two option cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Describe differences */}
        <button
          type="button"
          onClick={() => onSetView('describe')}
          className="flex flex-col items-center gap-3 rounded-lg border-2 border-gray-300 bg-white p-5 text-center transition-all hover:border-primary hover:bg-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-light">
            <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div>
            <p className="font-semibold text-editorial-black">Describe what&apos;s different</p>
            <p className="mt-1 text-xs text-gray-500">
              Tell us how your plant differs
            </p>
          </div>
        </button>

        {/* Retake photo */}
        <button
          type="button"
          onClick={() => onSetView('retake')}
          className="flex flex-col items-center gap-3 rounded-lg border-2 border-gray-300 bg-white p-5 text-center transition-all hover:border-primary hover:bg-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-light">
            <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <p className="font-semibold text-editorial-black">Retake your photo</p>
            <p className="mt-1 text-xs text-gray-500">
              Try again with better tips
            </p>
          </div>
        </button>
      </div>

      {/* Manual entry link */}
      <button
        type="button"
        onClick={onManualEntry}
        className="flex w-full items-center justify-center gap-2 rounded-lg px-6 py-3 font-medium text-primary transition-colors hover:bg-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span>Enter name manually instead</span>
      </button>
    </div>
  );

  // Render describe view
  const renderDescribe = () => (
    <div className="space-y-6">
      {/* Context */}
      <div className="rounded-lg bg-primary-light p-4">
        <h3 className="mb-2 font-semibold text-primary">Describe what&apos;s different</h3>
        <p className="text-sm text-gray-500">
          Your plant was identified as <span className="font-medium text-editorial-black">{wrongIdentificationName}</span>.
          What looks different about your plant?
        </p>
      </div>

      {/* Example prompts */}
      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Examples:
        </p>
        <div className="flex flex-wrap gap-2">
          {DESCRIPTION_EXAMPLES.map((example, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setDescription(example)}
              className="min-h-[44px] rounded-full bg-gray-100 px-4 py-2.5 text-xs text-gray-500 transition-colors hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {example}
            </button>
          ))}
        </div>
      </div>

      {/* Text input */}
      <div className="space-y-2">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe how your plant is different..."
          rows={4}
          maxLength={500}
          className="w-full rounded-lg border-2 border-gray-300 p-3 text-editorial-black placeholder-gray-400 transition-colors focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          aria-label="Plant description"
        />
        <div className="flex items-center justify-between text-xs">
          <span className={description.length < MIN_DESCRIPTION_LENGTH ? 'text-gray-500' : 'text-success'}>
            {description.length < MIN_DESCRIPTION_LENGTH
              ? `${description.length} / ${MIN_DESCRIPTION_LENGTH} minimum characters`
              : `${description.length}/500`}
          </span>
          {description.length >= MIN_DESCRIPTION_LENGTH && (
            <span className="text-success">Ready to submit</span>
          )}
        </div>
      </div>

      {/* Submit button */}
      <button
        type="button"
        onClick={handleSubmitDescription}
        disabled={description.trim().length < MIN_DESCRIPTION_LENGTH || isProcessing}
        className="flex w-full h-btn-lg min-h-btn-lg items-center justify-center gap-2 rounded-lg bg-primary px-6 font-medium text-white transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isProcessing ? (
          <>
            <span className="h-5 w-5 animate-spin motion-reduce:animate-none rounded-full border-2 border-white border-t-transparent" />
            <span>Analyzing...</span>
          </>
        ) : (
          <>
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span>Try again</span>
          </>
        )}
      </button>

      {/* Back to options */}
      <button
        type="button"
        onClick={() => onSetView('options')}
        disabled={isProcessing}
        className="w-full rounded-lg border-2 border-gray-300 px-6 py-3 font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-editorial-black focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Back to options
      </button>
    </div>
  );

  // Render retake view with guided tips
  const renderRetake = () => (
    <div className="space-y-6">
      {/* Tips header */}
      <div className="rounded-lg bg-primary-light p-4">
        <h3 className="mb-2 font-semibold text-primary">Tips for a better photo</h3>
        <p className="text-sm text-gray-500">
          Follow these tips for the best chance of a correct identification.
        </p>
      </div>

      {/* Guided tips */}
      <div className="space-y-3">
        {RETAKE_TIPS.map((tip, index) => (
          <div key={index} className="flex gap-3 rounded-lg bg-white p-3">
            <div className="mt-0.5">
              {tip.icon}
            </div>
            <div>
              <p className="text-sm font-medium text-editorial-black">{tip.title}</p>
              <p className="text-xs text-gray-500">{tip.description}</p>
            </div>
          </div>
        ))}

        {/* Additional quick tips */}
        <div className="flex gap-3 rounded-lg border border-dashed border-gray-300 p-3">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-editorial-black">Use good lighting</p>
            <p className="text-xs text-gray-500">Natural daylight, avoid flash. Overcast days are ideal. Put your hand behind the plant to help the camera focus.</p>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handlePhotoSelect}
        className="hidden"
        aria-label="Select plant photo"
      />

      {/* Camera and gallery buttons */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => openFilePicker('environment')}
          disabled={isUploadingPhoto || isProcessing}
          className="flex w-full h-btn-lg min-h-btn-lg items-center justify-center gap-3 rounded-lg border-2 border-primary bg-primary-light px-6 text-primary transition-all hover:bg-primary hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isUploadingPhoto ? (
            <>
              <span className="h-6 w-6 animate-spin motion-reduce:animate-none rounded-full border-2 border-current border-t-transparent" />
              <span className="font-medium">Processing...</span>
            </>
          ) : (
            <>
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="font-medium">Take a new photo</span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={() => openFilePicker()}
          disabled={isUploadingPhoto || isProcessing}
          className="flex w-full items-center justify-center gap-3 rounded-lg border-2 border-gray-300 px-6 py-4 text-editorial-black transition-all hover:border-primary hover:bg-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="font-medium">Choose from gallery</span>
        </button>
      </div>

      {/* Photo error */}
      {photoError && (
        <p className="text-sm text-danger" role="alert">{photoError}</p>
      )}

      {/* Back to options */}
      <button
        type="button"
        onClick={() => onSetView('options')}
        disabled={isProcessing}
        className="w-full rounded-lg border-2 border-gray-300 px-6 py-3 font-medium text-gray-500 transition-colors hover:border-gray-400 hover:text-editorial-black focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Back to options
      </button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Error display */}
      {error && (
        <div className="rounded-lg bg-danger/10 p-4 text-danger" role="alert">
          {error}
        </div>
      )}

      {/* Recovery view content */}
      {recoveryStep === 'options' && renderOptions()}
      {recoveryStep === 'describe' && renderDescribe()}
      {recoveryStep === 'retake' && renderRetake()}
    </div>
  );
}
